import { ArrowBack, ArrowForward } from '@mui/icons-material';
import {
  ButtonBase,
  IconButton as MuiIconButton,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import { useMap } from '@vis.gl/react-google-maps';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import {
  activeViewAllGroup,
  anchoredPartnerCardId,
  mapMarkerByKeyFamily,
  selectedPartnerMapTeamId,
  teamDetailPanelVisible,
  viewAllTeamId,
} from '@/common/atoms/community';
import { useGetTeamOrganization } from '../../hooks/useGetTeamOrganization';
import { mailingAddressHasValidGeolocation } from '../../utils/location';
import { MarkerPopover } from '../MarkerPopover';
import {
  sortTeamsByGroupKey,
  type TeamWithMailingAddress,
} from './TeamLocationsStepper.helpers';

const IconButton = styled(MuiIconButton)(({ theme }) => ({
  color: theme.palette.primary.main,
  '& . Mui-disabled': {
    color: theme.palette.action.disabled,
  },
}));

export function TeamLocationsStepper() {
  const groupKey = useAtomValue(activeViewAllGroup);
  const viewAllTeam = useAtomValue(viewAllTeamId);
  const open = !!groupKey && !!viewAllTeam;
  const { teams: orgTeams } = useGetTeamOrganization(open ? viewAllTeam : null);
  const teams = useMemo(
    () =>
      sortTeamsByGroupKey(
        (orgTeams ?? []).filter((team): team is TeamWithMailingAddress =>
          mailingAddressHasValidGeolocation(team.mailingAddress),
        ),
      ),
    [orgTeams],
  );
  const map = useMap();
  const currentTeamId = useAtomValue(selectedPartnerMapTeamId);
  const currentIndex = teams.findIndex(({ id }) => id === currentTeamId);
  const currentTeam = teams[currentIndex];
  const mapMarker = useAtomValue(
    mapMarkerByKeyFamily(groupKey ?? ''),
  ) as google.maps.marker.AdvancedMarkerElement | null;

  const setSelectedPartnerMapTeamId = useSetAtom(selectedPartnerMapTeamId);
  const setAnchoredPartnerCardId = useSetAtom(anchoredPartnerCardId);
  const setTeamDetailPanelVisible = useSetAtom(teamDetailPanelVisible);
  const setActiveViewAllGroup = useSetAtom(activeViewAllGroup);

  const handlePopupClick = useCallback(() => {
    if (!currentTeam) return;
    setSelectedPartnerMapTeamId(currentTeam.id);
    setAnchoredPartnerCardId(currentTeam.id);
    setTeamDetailPanelVisible(true);
    setActiveViewAllGroup(undefined);
  }, [
    currentTeam,
    setSelectedPartnerMapTeamId,
    setAnchoredPartnerCardId,
    setTeamDetailPanelVisible,
    setActiveViewAllGroup,
  ]);

  const goToTeam = useCallback(
    (index: number) => {
      const teamIndex = index < 0 ? teams.length - 1 : index % teams.length;
      const { id, mailingAddress } = teams[teamIndex];
      const newGroupKey = `${mailingAddress.geolocationLatitude}_${mailingAddress.geolocationLongitude}`;

      setActiveViewAllGroup(newGroupKey);
      setSelectedPartnerMapTeamId(id);
      setAnchoredPartnerCardId(id);

      if (map && newGroupKey !== groupKey) {
        const bounds = map.getBounds();
        const teamLatLng = {
          lat: Number(mailingAddress.geolocationLatitude),
          lng: Number(mailingAddress.geolocationLongitude),
        };
        const outOfBounds = bounds && !bounds.contains(teamLatLng);
        const markerInCluster =
          mapMarker &&
          mapMarker instanceof google.maps.marker.AdvancedMarkerElement &&
          mapMarker.map === null;
        if (outOfBounds || markerInCluster) {
          map.setCenter(teamLatLng);
          if (markerInCluster) {
            map.setZoom(17);
          }
        }
      }
    },
    [
      teams,
      map,
      groupKey,
      mapMarker,
      setActiveViewAllGroup,
      setSelectedPartnerMapTeamId,
      setAnchoredPartnerCardId,
    ],
  );

  const handleClose = useCallback(() => {
    setActiveViewAllGroup(undefined);
    setSelectedPartnerMapTeamId(undefined);
    setAnchoredPartnerCardId(undefined);
  }, [
    setActiveViewAllGroup,
    setSelectedPartnerMapTeamId,
    setAnchoredPartnerCardId,
  ]);

  if (!mapMarker || !open || !currentTeam) {
    return null;
  }

  return (
    <MarkerPopover anchor={mapMarker} onClose={handleClose}>
      <Stack
        component={ButtonBase}
        disableRipple
        sx={{
          display: 'flex',
          alignItems: 'start',
          width: '100%',
        }}
        onClick={handlePopupClick}
      >
        <Stack
          sx={{ gap: 0.5, p: 2, alignItems: 'flex-start', textAlign: 'left' }}
        >
          <Typography variant="h5" sx={{ color: 'primary.main' }}>
            {currentTeam.legalName ?? currentTeam.alias}
          </Typography>
          {currentTeam.alias && currentTeam.legalName !== currentTeam.alias && (
            <Typography
              variant="caption"
              sx={{ pt: 0.5, pb: 1, color: 'text.secondary' }}
            >
              {currentTeam.alias}
            </Typography>
          )}
          <Typography variant="body3">
            {currentTeam.mailingAddress.formattedAddress}
          </Typography>
        </Stack>
        <Stack
          direction="row"
          sx={{
            pb: 1,
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            px: 1,
          }}
        >
          <IconButton
            aria-label="previous team"
            onClick={(e) => {
              e.stopPropagation();
              goToTeam(currentIndex - 1);
            }}
          >
            <ArrowBack fontSize="small" />
          </IconButton>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {currentIndex + 1} of {teams.length}
          </Typography>
          <IconButton
            aria-label="next team"
            onClick={(e) => {
              e.stopPropagation();
              goToTeam(currentIndex + 1);
            }}
          >
            <ArrowForward fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>
    </MarkerPopover>
  );
}
